import { Container, Overview, Hotspots, Logo } from './Map.styles';
import { Fragment, useState, useRef, useEffect } from 'react';
import { getCampaignGames } from '@/redux/campaign/selectors';
import LocalizedImage from '@CS/components/LocalizedImage';
import { FallbackHR, FallbackEN } from '@/assets/images';
import type { CampaignGame } from '@/services/api/types';
import Deals from '@/assets/icons/deals.svg?component';
import { useLoader, useTranslation } from '@CS/hooks';
import Game from '@/assets/icons/game.svg?component';
import Shop from '@/assets/icons/shop.svg?component';
import { DELTA_UPDATE } from '@CS/utils/Number';
import { isIOS } from '@CS/utils/Device';
import { useMapVideo } from '@/hooks';
import { connect } from 'react-redux';
import HOTSPOTS from './hotspots';
import Content from './content';

type MapProps = {
  setMapLoaded: (loaded: boolean) => void;
  onClick: (index: number) => void;
  ratio?: string | number;
  games: CampaignGame[];
  step?: number;
};

const Map = ({
  setMapLoaded,
  onClick,
  games,
  step,
  ratio = 2.25
}: MapProps) => {
  const videoUrl = useMapVideo(3);
  const logo = useTranslation(Content);
  const map = useRef<HTMLDivElement>(null);

  const [scroll, setScroll] = useState(false);
  const [loading, setLoading] = useState(true);

  const videoRef = useRef<HTMLVideoElement>(null);
  const loader = useLoader(loading, { image: true });

  const handleVideoLoad = () => ~setMapLoaded(true) && setLoading(false);

  useEffect(() => {
    if (step === undefined || loading) return;
    const width = innerHeight * +ratio - innerWidth;
    map.current!.scrollLeft = (width * HOTSPOTS[step].scroll) | 0;
  }, [step, loading]);

  useEffect(() => {
    if (!loading) {
      map.current!.scrollLeft = ((innerHeight * +ratio - innerWidth) * 0.5) | 0;
      setTimeout(() => setScroll(true), DELTA_UPDATE);
    }
  }, [loading]);

  useEffect(() => {
    if (!isIOS) return;

    const handlePlayVideo = () =>
      setTimeout(() => videoRef.current?.play(), 50);

    const handlePauseVideo = () => videoRef.current?.pause();

    const handleVisibilityChange = () =>
      document.visibilityState === 'hidden'
        ? handlePauseVideo()
        : handlePlayVideo();

    window.addEventListener('focus', handlePlayVideo);
    window.addEventListener('blur', handlePauseVideo);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handlePlayVideo);
      window.removeEventListener('blur', handlePauseVideo);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <Container
      ref={map}
      $ratio={ratio}
      $scroll={scroll}
      $translate={step === 1}
    >
      {loader}

      <Overview
        onCanPlayThrough={handleVideoLoad}
        onLoadedData={handleVideoLoad}
        onError={handleVideoLoad}
        draggable="false"
        ref={videoRef}
        playsInline
        autoPlay
        muted
        loop
      >
        <source src={`${videoUrl}.mp4`} type="video/mp4" />
        <source src={`${videoUrl}.webm`} type="video/webm" />
      </Overview>

      {!loading && (
        <LocalizedImage
          images={{ hr: FallbackHR, en: FallbackEN }}
          customStyle={{
            width: 'calc(100dvh * var(--ratio))',
            position: 'relative',
            maxWidth: 'unset',
            height: '100dvh',
            top: '0px'
          }}
        />
      )}

      <Logo alt="Golden Getaway" src={logo} />

      <Hotspots>
        {HOTSPOTS.map(({ offset = [0, 0], size, name, type, x, y }, h) => {
          const active =
            ((type === 'shop' || type === 'deals') && true) ||
            !!games.find(
              ({ active, isStarted, isFinished, slug }) =>
                active && isStarted && !isFinished && slug.includes(name ?? '')
            );

          return (
            <Fragment key={name || type}>
              <div
                onClick={onClick.bind(null, h)}
                style={{
                  pointerEvents: (active && 'all') || 'none',
                  left: `${(x - offset[0]) * 100}%`,
                  top: `${(y - offset[1]) * 100}%`,
                  height: `${size[1]}px`,
                  width: `${size[0]}px`,
                  position: 'absolute'
                }}
              />

              {type === 'shop' ? (
                <Shop
                  style={{ top: `${y * 100}%`, left: `${x * 100}%` }}
                  onClick={onClick.bind(null, h)}
                />
              ) : type === 'deals' ? (
                <Deals
                  style={{ top: `${y * 100}%`, left: `${x * 100}%` }}
                  onClick={onClick.bind(null, h)}
                />
              ) : (
                <Game
                  onClick={onClick.bind(null, h)}
                  style={{
                    fill: active ? '#863399' : 'rgba(44,45,49,0.91)',
                    opacity: +active * 0.3 + 0.7,
                    left: `${x * 100}%`,
                    top: `${y * 100}%`
                  }}
                />
              )}
            </Fragment>
          );
        })}
      </Hotspots>
    </Container>
  );
};

const mapStateToProps = (state: never) => ({
  games: getCampaignGames(state)
});

export default connect(mapStateToProps)(Map);
